// /app/frontend/src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Simple, robust API call function
 * - No response cloning (which causes "body already used" errors)
 * - Simple retry with fresh requests
 * - Proper error handling
 */
const apiCall = async (url, options = {}) => {
  const maxRetries = 2;
  let lastError = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Read the response body ONCE
      const text = await response.text();
      
      // Try to parse as JSON
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        // Not JSON, that's okay
        data = { message: text };
      }

      if (response.ok) {
        return { success: true, data, status: response.status };
      } else {
        // Non-OK response - extract error message
        const errorMsg = data?.detail || data?.message || `Request failed (${response.status})`;
        return { success: false, error: errorMsg, status: response.status, data };
      }
    } catch (err) {
      clearTimeout(timeoutId);
      lastError = err;

      // Determine if we should retry
      const isNetworkError = err.name === 'TypeError' || err.name === 'AbortError';
      const shouldRetry = isNetworkError && attempt < maxRetries;

      if (shouldRetry) {
        // Wait before retry (exponential backoff)
        await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
        continue;
      }

      // Final attempt failed
      return { 
        success: false, 
        error: err.message || 'Network error. Please check your connection.',
        status: 0 
      };
    }
  }

  return { 
    success: false, 
    error: lastError?.message || 'Request failed after retries',
    status: 0 
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (token) => {
    const result = await apiCall(`${API_URL}/api/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (result.success && result.data) {
      setUser(result.data);
    } else {
      // Token invalid or expired
      localStorage.removeItem('auth_token');
      setUser(null);
    }
    setLoading(false);
  };

  const sendMagicLink = async (email) => {
    setError(null);

    const result = await apiCall(`${API_URL}/api/auth/send-magic-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    if (result.success) {
      return { 
        success: true, 
        debugLink: result.data?.debug_link,
        message: result.data?.message || 'Magic link sent!'
      };
    } else {
      setError(result.error);
      return { success: false, error: result.error };
    }
  };

  const verifyMagicLink = async (token) => {
    setError(null);

    const result = await apiCall(`${API_URL}/api/auth/verify-magic-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    });

    if (result.success && result.data?.token) {
      localStorage.setItem('auth_token', result.data.token);
      setUser(result.data.user);
      return { success: true };
    } else {
      const errorMsg = result.error || 'Invalid or expired link';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    setUser(null);
  }, []);

  const refreshUser = async () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      await fetchUser(token);
    }
  };

  // Check if user has Pro access
  const isPro = user?.plan === 'pro' && user?.subscriptionStatus === 'active';

  // Check feature access
  const canAccessFeature = (feature) => {
    if (isPro) return true;
    
    // Free plan restrictions
    const freeFeatures = ['overview', 'pile-makeup', 'soil-profile', 'results', '3d-view', 'settings'];
    return freeFeatures.includes(feature);
  };

  const value = {
    user,
    loading,
    error,
    setError,
    sendMagicLink,
    verifyMagicLink,
    logout,
    refreshUser,
    isPro,
    canAccessFeature,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
